import React, { useState, useEffect, useContext } from 'react';
import { StoreContext } from '../../Hooks/store';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import {
  ListItemButton,
  ListItemText,
  TextField,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import EditIcon from '@mui/icons-material/Edit';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { cloneTodoList } from '../../../api/todo';

// Mobile menu component for actions
const MobileActionsMenu = ({
  todolist,
  localArchived,
  setLocalArchived,
  localListName, // Added localListName
  setLocalListName, // Added setLocalListName
  editingListId, // Added editingListId
  handleEditListName,
  handleSaveListName,
  handleArchiveTodoList,
  handleDeleteConfirmation,
  showSnackbar,
  queryClient
}) => {
  const storeContext = useContext(StoreContext);
  const [, setShowComments] = storeContext.showComments;
  const [, setCommentsListId] = storeContext.commentsListId;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-label="more"
        aria-controls={open ? 'todo-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="todo-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose} // Added this to close menu on item click as well if not handled by item
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleEditListName(todolist.id, todolist.list_name);
          handleClose(e); // Explicitly close after action
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit List
        </MenuItem>

        <MenuItem onClick={async (e) => {
          e.stopPropagation();
          handleClose(e); // Explicitly close before async action
          const newArchived = !localArchived;
          setLocalArchived(newArchived);
          try {
            const result = await handleArchiveTodoList(todolist.id, newArchived);
            if (!result) {
              throw new Error('Archive/Unarchive operation failed - no result returned');
            }
            if (result.archived !== undefined) {
              setLocalArchived(result.archived);
            }
            if (typeof showSnackbar === 'function') {
              showSnackbar(`List ${newArchived ? 'archived' : 'unarchived'} successfully`, 'success');
            }
          } catch (error) {
            setLocalArchived(todolist.archived); // Rollback
            if (typeof showSnackbar === 'function') {
              showSnackbar(`Failed to ${newArchived ? 'archive' : 'unarchive'} list: ${error.message}`, 'error');
            }
          }
        }}>
          <ListItemIcon>
            {localArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
          </ListItemIcon>
          {localArchived ? 'Unarchive List' : 'Archive List'}
        </MenuItem>

        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleClose(e); // Explicitly close
          const { isTransitioning, disableTodoComments: [disableTodoComments], showTodos: [, setShowTodos] } = storeContext;
          if (isTransitioning || disableTodoComments) {
            return;
          }
          setCommentsListId(todolist.id);
          setTimeout(() => {
            setShowComments(true);
            setShowTodos(false);
          }, 50);
        }}>
          <ListItemIcon>
            <CommentIcon fontSize="small" />
          </ListItemIcon>
          Comments / Summary
        </MenuItem>

        <MenuItem onClick={async (e) => {
          e.stopPropagation();
          handleClose(e); // Close the menu immediately

          try {
            const [userId] = storeContext.thisuserid;
            const newList = await cloneTodoList(userId, todolist.id, todolist.list_name);
            const { mytodolists } = storeContext;
            const [, settodolists] = mytodolists;
            if (settodolists && typeof settodolists === 'function') {
              settodolists(prevLists => [newList, ...prevLists.filter(list => list.id !== newList.id)]);
            }
            await queryClient.resetQueries(['todoLists', userId]);
            await queryClient.refetchQueries(['todoLists', userId], {
              active: true,
              inactive: true,
              stale: true
            });

            if (typeof showSnackbar === 'function') {
              setTimeout(() => {
                showSnackbar(`List cloned successfully. You can now edit the name.`, 'success');
              }, 50); // Delay from previous fix attempt, keeping for consistency
            }
            if (typeof handleEditListName === 'function') {
              setTimeout(() => {
                handleEditListName(newList.id, newList.list_name);
              }, 100);
            }
          } catch (error) {
            console.error('Error cloning todo list:', error);
            if (typeof showSnackbar === 'function') {
              setTimeout(() => {
                showSnackbar(`Failed to clone list: ${error.message}`, 'error');
              }, 50); // Delay from previous fix attempt
            }
          }
        }}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          Clone List
        </MenuItem>

        <MenuItem onClick={async (e) => {
          e.stopPropagation();
          handleClose(e); // Explicitly close
          setLocalListName('');
          setLocalArchived(true);
          try {
            await handleDeleteConfirmation(todolist.id);
            // No snackbar here, parent (ArchivedTodolists) handles it on successful delete
          } catch (error) {
            setLocalListName(todolist.list_name); // Rollback
            setLocalArchived(todolist.archived); // Rollback
            if (typeof showSnackbar === 'function') {
              showSnackbar(`Failed to delete list: ${error.message}`, 'error');
            }
          }
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete List
        </MenuItem>
      </Menu>
    </>
  );
};

const TodoListItem = ({
  todolist,
  todoListCounts,
  onSelectTodoList,
  handleArchiveTodoList,
  handleDeleteConfirmation,
  handleEditListName,
  handleSaveListName,
  editingListId,
  setEditingListId,
  editedListName,
  setEditedListName,
  classes,
  settodolistid,
  setTodoHeader,
  showSnackbar
}) => {
  const storeContext = useContext(StoreContext);
  const [, setShowComments] = storeContext.showComments;
  const [, setCommentsListId] = storeContext.commentsListId;
  const { total, completed } = todoListCounts[todolist.id] || { total: 0, completed: 0 };
  const [localListName, setLocalListName] = useState(todolist.list_name);
  const [localDescription, setLocalDescription] = useState(todolist.description || '');
  const [localArchived, setLocalArchived] = useState(todolist.archived);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const queryClient = useQueryClient();

  useEffect(() => {
    setLocalListName(todolist.list_name);
  }, [todolist.list_name]);

  useEffect(() => {
    setLocalDescription(todolist.description || '');
  }, [todolist.description]);

  useEffect(() => {
    setLocalArchived(todolist.archived);
  }, [todolist.archived]);

  const handleCloneList = async (e) => {
    e.stopPropagation(); // Crucial: stop propagation for the IconButton's click
    try {
      const [userId] = storeContext.thisuserid;
      const newList = await cloneTodoList(userId, todolist.id, todolist.list_name);
      const { mytodolists } = storeContext;
      const [, settodolists] = mytodolists;
      if (settodolists && typeof settodolists === 'function') {
        settodolists(prevLists => [newList, ...prevLists.filter(list => list.id !== newList.id)]);
      }
      await queryClient.resetQueries(['todoLists', userId]);
      await queryClient.refetchQueries(['todoLists', userId], {
        active: true,
        inactive: true,
        stale: true
      });

      if (typeof showSnackbar === 'function') {
         setTimeout(() => {
          showSnackbar(`List cloned successfully. You can now edit the name.`, 'success');
        }, 50); // Adjusted delay
      }
      if (typeof handleEditListName === 'function') {
        setTimeout(() => {
          handleEditListName(newList.id, newList.list_name);
        }, 100); // Original delay
      }
    } catch (error) {
      console.error('Error cloning todo list:', error);
      if (typeof showSnackbar === 'function') {
        setTimeout(() => {
          showSnackbar(`Failed to clone list: ${error.message}`, 'error');
        }, 50); // Adjusted delay
      }
    }
  };

  return (
    <ListItemButton
      key={todolist.id}
      onClick={() => { // This is the main navigation click
        settodolistid(todolist.id);
        setTodoHeader(localListName);
        if (typeof onSelectTodoList === 'function') {
          onSelectTodoList(todolist.id, localListName);
        }
      }}
      className={todolist.archived ? classes.archivedItem : ''}
      sx={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'nowrap',
        width: '100%',
        minWidth: { xs: '300px', sm: '400px', md: '500px' },
        pr: { xs: 1, sm: 2 }
      }}
    >
      {/* Main content area (list name and description) */}
      <Box sx={{
        minWidth: { xs: '100px', sm: '120px', md: '180px' },
        maxWidth: '100%',
        flexGrow: 1,
        mr: 2,
        overflow: 'hidden',
      }}>
        {editingListId === todolist.id ? (
          <Box>
            <TextField
              autoFocus
              fullWidth
              multiline
              value={editedListName}
              onChange={(e) => {
                setEditedListName(e.target.value);
                setLocalListName(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                minWidth: { xs: '150px', sm: '200px' },
                mb: 1
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Add a description (optional)"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                minWidth: { xs: '150px', sm: '200px' }
              }}
            />
          </Box>
        ) : (
          <ListItemText
            primary={`${localListName} (${completed}/${total})`}
            secondary={
              <Box>
                {localArchived && <Typography variant="body2" color="textSecondary">Archived</Typography>}
                {localDescription && (
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{
                      mt: 0.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {localDescription}
                  </Typography>
                )}
              </Box>
            }
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflow: 'visible',
            }}
          />
        )}
      </Box>

      {/* Action buttons area */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {editingListId === todolist.id ? (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveListName(todolist.id, localDescription);
                setEditingListId(null);
              }}
              title="Save"
            >
              <SaveOutlinedIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingListId(null);
                setEditedListName(todolist.list_name);
                setLocalDescription(todolist.description || '');
              }}
              title="Cancel"
            >
              <CancelOutlinedIcon />
            </IconButton>
          </>
        ) : (
          // Normal mode buttons (desktop or mobile)
          isDesktop ? (
            // Desktop view
            <div
              className={classes.actionButtons}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexWrap: 'nowrap',
                marginRight: 0
              }}
              onClick={(e) => e.stopPropagation()} // <<< ADDED FIX: Stop propagation on the container
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // Keep as defense-in-depth
                  handleEditListName(todolist.id, todolist.list_name);
                }}
              >
                <EditIcon />
              </IconButton>
              <Tooltip title={localArchived ? "Unarchive list" : "Archive list"}>
                <IconButton
                  onClick={async (e) => {
                    e.stopPropagation(); // Keep as defense-in-depth
                    const newArchived = !localArchived;
                    setLocalArchived(newArchived); // Optimistic update
                    try {
                      const result = await handleArchiveTodoList(todolist.id, newArchived);
                      if (!result) {
                        throw new Error('Archive/Unarchive operation failed - no result returned');
                      }
                      if (result.archived !== undefined) {
                        setLocalArchived(result.archived);
                      }
                      if (typeof showSnackbar === 'function') {
                        showSnackbar(`List ${newArchived ? 'archived' : 'unarchived'} successfully`, 'success');
                      }
                    } catch (error) {
                      console.error('Archive/Unarchive failed:', error);
                      setLocalArchived(todolist.archived); // Rollback optimistic update
                      if (typeof showSnackbar === 'function') {
                        showSnackbar(`Failed to ${newArchived ? 'archive' : 'unarchive'} list: ${error.message}`, 'error');
                      }
                    }
                  }}
                  data-testid={`archive-button-${todolist.id}`}
                >
                  {localArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Comments / Summary">
                <IconButton
                  onClick={async (e) => {
                    e.stopPropagation(); // Keep as defense-in-depth
                    const { isTransitioning, disableTodoComments: [disableTodoComments], showTodos: [, setShowTodos] } = storeContext;
                    if (isTransitioning || disableTodoComments) return;
                    React.startTransition(() => {
                      setCommentsListId(todolist.id);
                      setShowComments(true);
                      setShowTodos(false);
                    });
                  }}
                >
                  <CommentIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone List">
                {/* handleCloneList already contains e.stopPropagation() */}
                <IconButton onClick={handleCloneList}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={async (e) => {
                  e.stopPropagation(); // Keep as defense-in-depth
                  // Optimistically update local state first (visual cue)
                  const originalName = localListName;
                  const originalArchived = localArchived;
                  setLocalListName(''); 
                  setLocalArchived(true);
                  try {
                    await handleDeleteConfirmation(todolist.id);
                    // On successful deletion, ArchivedTodolists will refetch and remove.
                    // No snackbar here, parent handles it.
                  } catch (error) {
                    // Rollback optimistic updates if deletion fails or is cancelled by user in dialog
                    setLocalListName(originalName);
                    setLocalArchived(originalArchived);
                    // Parent (ArchivedTodolists) shows error if needed, or dialog handles cancellation.
                  }
                }}
                disabled={!localListName}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ) : (
            // Mobile view with more menu
            <MobileActionsMenu
              todolist={todolist}
              localArchived={localArchived}
              setLocalArchived={setLocalArchived}
              localListName={localListName} // Pass localListName
              setLocalListName={setLocalListName} // Pass setLocalListName
              editingListId={editingListId} // Pass editingListId
              handleEditListName={handleEditListName}
              handleSaveListName={handleSaveListName}
              handleArchiveTodoList={handleArchiveTodoList}
              handleDeleteConfirmation={handleDeleteConfirmation}
              showSnackbar={showSnackbar}
              queryClient={queryClient}
            />
          )
        )}
      </Box>
    </ListItemButton>
  );
};

TodoListItem.propTypes = {
  todolist: PropTypes.shape({
    id: PropTypes.number.isRequired,
    list_name: PropTypes.string.isRequired,
    description: PropTypes.string,
    archived: PropTypes.bool.isRequired,
  }).isRequired,
  todoListCounts: PropTypes.object,
  onSelectTodoList: PropTypes.func,
  handleArchiveTodoList: PropTypes.func.isRequired, // Made required based on usage
  handleDeleteConfirmation: PropTypes.func.isRequired, // Made required
  handleEditListName: PropTypes.func.isRequired, // Made required
  handleSaveListName: PropTypes.func.isRequired, // Made required
  editingListId: PropTypes.number,
  setEditingListId: PropTypes.func.isRequired, // Made required
  editedListName: PropTypes.string,
  setEditedListName: PropTypes.func.isRequired, // Made required
  classes: PropTypes.object.isRequired,
  settodolistid: PropTypes.func.isRequired, // Made required
  setTodoHeader: PropTypes.func.isRequired, // Made required
  showSnackbar: PropTypes.func,
};

TodoListItem.defaultProps = {
  onSelectTodoList: () => {},
  todoListCounts: {},
  // Removed defaults for functions now marked as required if they are essential for operation
  editingListId: null,
  editedListName: '',
  showSnackbar: () => {}, // Keep for non-critical notifications if any
};

export default TodoListItem;